import {Controller} from "@hotwired/stimulus";

export default class extends Controller {
  static targets = ["item"];

  // Stimulus fires itemTargetConnected for targets that were already in the markup, so a
  // whole-thread re-render (every send replaces the panel body) announces EVERY historic
  // message. Without this flag each old AI message schedules its own scrollIntoView and the
  // view jumps back to the top of some earlier answer — the "sending scrolls me to the top" bug.
  initialize() {
    this._ready = false
  }

  connect() {
    this._userScrolled = false
    this._isStreaming = false

    // On connect (opening a chat), scroll to bottom to show latest messages
    this.scrollToBottom()
    // ...and again after layout: on a fresh insert scrollHeight is not final yet, so the first
    // call can land at 0. Only after this frame do individual messages get to move the view.
    requestAnimationFrame(() => {
      this.scrollToBottom()
      this._ready = true
    })

    // Track if user manually scrolled up
    this._onScroll = () => {
      const el = this.element
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight
      // If user scrolled more than 100px from bottom, they're reading history
      this._userScrolled = distanceFromBottom > 100
    }
    this.element.addEventListener('scroll', this._onScroll)

    // Watch for streaming updates (loader steps, partial content)
    this.observer = new MutationObserver(() => {
      // Only auto-scroll if we're streaming AND user hasn't scrolled up
      if (this._isStreaming && !this._userScrolled) {
        this.scrollToBottom()
      }
    })
    this.observer.observe(this.element, { childList: true, subtree: true })
  }

  disconnect() {
    this.observer?.disconnect()
    this.element.removeEventListener('scroll', this._onScroll)
  }

  itemTargetConnected(element) {
    // Initial render — connect() owns the scroll position.
    if (!this._ready) return

    // A new message was added (user or AI)
    if (element.querySelector('.laz-av')) {
      // AI response — scroll to show the start of the response, then track streaming
      this._isStreaming = true
      this._userScrolled = false
      setTimeout(() => {
        element.scrollIntoView({ behavior: 'smooth', block: 'start' })
      }, 100)
    } else {
      // User message — always scroll to bottom
      this._userScrolled = false
      this._isStreaming = true
      this.scrollToBottom()
    }
  }

  // Call this when streaming is done (e.g., final response rendered)
  streamingDone() {
    this._isStreaming = false
  }

  scrollToBottom() {
    this.element.scrollTop = this.element.scrollHeight
  }
}
